import type { Streaks, TranslationCode } from "@versedrill/core";
import { DEFAULT_TRANSLATION, isTranslationCode } from "@versedrill/core";
import { localDayKey } from "@versedrill/core";
import { addPlayedDay, effectiveStreak, seedPlayedDays, streaksFromDays } from "../activity.ts";
import type { SentRecord } from "../store.ts";

export const SYNC_FORMAT = 1;
export const MAX_SENT_KEPT = 50;

/** What travels between devices. Nothing device-specific (theme, reminder, push subscription, anonymous player id). */
export interface SyncPayload {
  v: typeof SYNC_FORMAT;
  /** When this payload was produced (device clock). Decides which side's preferences win. */
  updatedAt: number;
  /** The sender's live streak, so a merge can never shorten it even if its list of days were incomplete. Optional: older copies lack it. */
  streak?: { current: number; lastDay: string | null };
  stars: Record<number, number>;
  playedDays: string[];
  longestStreak: number;
  received: Record<string, number>;
  sent: SentRecord[];
  senderName: string;
  translation: TranslationCode;
  changelogSeen: string | null;
  seenIntro: boolean;
}

/** The slice of the store that syncs. */
export type SyncSlice = Omit<SyncPayload, "v" | "updatedAt" | "longestStreak"> & { streaks: Streaks };

export function toPayload(s: SyncSlice, now: number): SyncPayload {
  return {
    v: SYNC_FORMAT,
    updatedAt: now,
    stars: s.stars,
    playedDays: s.playedDays,
    longestStreak: s.streaks.longestStreak,
    streak: { current: s.streaks.currentStreak, lastDay: s.streaks.currentStreakStart ? localDayKey(new Date(s.streaks.currentStreakStart)) : null },
    received: s.received,
    sent: s.sent,
    senderName: s.senderName,
    translation: s.translation,
    changelogSeen: s.changelogSeen,
    seenIntro: s.seenIntro,
  };
}

const maxOf = (a: Record<string, number>, b: Record<string, number>): Record<string, number> => {
  const out: Record<string, number> = { ...a };
  for (const [k, v] of Object.entries(b)) out[k] = Math.max(out[k] ?? 0, v);
  return out;
};

/** Reject anything that isn't the shape we wrote; a corrupt or hostile blob must not poison a device. */
export function isSyncPayload(x: unknown): x is SyncPayload {
  if (!x || typeof x !== "object") return false;
  const p = x as Record<string, unknown>;
  const numbers = (o: unknown) => !!o && typeof o === "object" && Object.values(o as object).every((v) => Number.isInteger(v) && (v as number) >= 1 && (v as number) <= 5);
  return (
    p.v === SYNC_FORMAT &&
    typeof p.updatedAt === "number" &&
    (p.streak === undefined || (typeof (p.streak as { current?: unknown }).current === "number" && ((p.streak as { lastDay?: unknown }).lastDay === null || /^\d{4}-\d{2}-\d{2}$/.test(String((p.streak as { lastDay?: unknown }).lastDay))))) &&
    numbers(p.stars) &&
    Array.isArray(p.playedDays) &&
    p.playedDays.every((d) => typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d)) &&
    typeof p.longestStreak === "number" &&
    numbers(p.received) &&
    Array.isArray(p.sent) &&
    p.sent.every((r) => r && typeof (r as SentRecord).code === "string" && typeof (r as SentRecord).manageToken === "string" && typeof (r as SentRecord).createdAt === "number" && typeof (r as SentRecord).reference === "string") &&
    typeof p.senderName === "string" &&
    isTranslationCode(p.translation) &&
    (p.changelogSeen === null || typeof p.changelogSeen === "string") &&
    typeof p.seenIntro === "boolean"
  );
}

/**
 * Two devices' progress become one. Progress is a union (nothing anyone earned is lost); preferences follow whichever
 * side was written more recently; streaks are recomputed from the combined days, so a streak kept alive on either
 * device counts on both.
 */
export function mergeSync(local: SyncSlice, localUpdatedAt: number, remote: SyncPayload, now: Date = new Date()): SyncSlice {
  const prefs = remote.updatedAt > localUpdatedAt ? remote : local;
  let playedDays = local.playedDays;
  for (const d of remote.playedDays) playedDays = addPlayedDay(playedDays, d);
  // A streak that is still alive on either device is a fact about consecutive days. Make sure those days are in the
  // combined list, so recounting can only keep it or lengthen it, never shorten it (e.g. a 19-day streak meeting a 2-day one).
  const today = localDayKey(now);
  const sides = [local.streaks, remote.streak ? { currentStreak: remote.streak.current, longestStreak: 0, currentStreakStart: remote.streak.lastDay ? new Date(`${remote.streak.lastDay}T12:00:00`).getTime() : 0 } : null];
  for (const s of sides) if (s && effectiveStreak(s, today) > 0) for (const d of seedPlayedDays(s)) playedDays = addPlayedDay(playedDays, d);

  const sentByCode = new Map<string, SentRecord>();
  for (const r of [...remote.sent, ...local.sent]) sentByCode.set(r.code, r); // local wins on a tie, it holds the freshest reference text
  const sent = [...sentByCode.values()].sort((a, b) => b.createdAt - a.createdAt).slice(0, MAX_SENT_KEPT);

  return {
    stars: maxOf(local.stars, remote.stars),
    playedDays,
    streaks: streaksFromDays(playedDays, Math.max(local.streaks.longestStreak, remote.longestStreak), now),
    received: maxOf(local.received, remote.received),
    sent,
    senderName: prefs.senderName || local.senderName || remote.senderName,
    translation: isTranslationCode(prefs.translation) ? prefs.translation : DEFAULT_TRANSLATION,
    changelogSeen: [local.changelogSeen, remote.changelogSeen].filter((x): x is string => x !== null).sort().at(-1) ?? null,
    seenIntro: local.seenIntro || remote.seenIntro,
  };
}
